import { Component } from "preact";
import PropTypes from "prop-types";
import { combineClasses, IS, Jiffy, QS } from "@green24/npm-javascript-utils";
import BoxTerminal from "../../../components/shared/BoxTerminal";
import ButtonsConstructor from "../../../components/shared/Input/Button/ButtonsConstructor";
import Interpret from "../../../components/shared/Localization/Interpret";
import { E_Route } from "../../../models/constants/Enum_Routes";
import { E_RentWizard_Section } from "../../../models/constants/Enums_RentWizard";
import { E_Keyboard_Type } from "../../../models/constants/Enums_Keyboard";
import withStorageUnit from "../../../components/shared/hoc/withStorageUnit";
import { StorageUtils } from "../../../utils/StorageUtils";
import PhoneNumber from "../../../components/shared/PhoneNumber";
import { sessionServices } from "../../../services/session-services";
import { Price, withLocalizationContext } from "../../../components/components";
import { route } from "preact-router";
import withConfiguration from "../../../components/shared/hoc/withConfiguration";
import { AppUtils } from "../../../utils/AppUtils";
import MessageModal from "../../../components/shared/Modal/MessageModal";
import { ErrorUtils } from "../../../utils/ErrorUtils";

import libphonenumber from "google-libphonenumber";
import { applicationThunk } from "../../../store/store";

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

class RentWizardContact extends Component {
	constructor() {
		super();

		this.state = {
			sending: false,
			email: '',
			phoneNumber: '',
		}
	}

	componentWillUnmount() {
		window.modal.close("error");
	}

	render({className, style, unit, serviceTypeID, bracketID}, {email, phoneNumber, sending}) {
		const serviceType = StorageUtils.findServiceTypeFromUnitByID(unit, serviceTypeID);
		if(!serviceType) return null;

		const bracket = StorageUtils.getTimeBracketByID(serviceType.tariff.timeBrackets, bracketID);
		if(!bracket) return null;

		return (
			<section className={combineClasses("rent-wizard-contact", className)} style={style}>
				<BoxTerminal
					title={"confirmSelection"}
					contentHeaderLeftSlot={
						<div className={"selected-service-type"}>
							<Interpret id={serviceType.titles}/>
						</div>
					}
					contentHeaderSlot={
						<div className={"selected-time-bracket"}>
							<Interpret id={"timeBracketUpToTime"} params={{
								price: <b><Price value={bracket.price} currency={serviceType.tariff.currency} removeFractionsIfZero={true}/></b>,
								time: <b>{Jiffy.readableDuration(bracket.maxMinutes * 60000)}</b>,
							}}/>
						</div>
					}
					contentLeftSlot={
						AppUtils.isDev &&
						<ButtonsConstructor buttons={[
							{
								className: "confirm-button dev-button",
								text: "test@test.cz",
								textTranslated: true,
								disabled: sending,
								action: () => this._sendPin("test@test.cz", undefined, true),
							}
						]}/>
					}
					contentSlot={
						<>
							<h2><Interpret id={"enterEmailOrPhoneNumber"}/></h2>

							<ButtonsConstructor buttons={[
								{
									text: email || "email",
									textTranslated: !!email,
									action: () => this._openEmailKeyboard(),
									disabled: sending,
								},
								{
									text: phoneNumber ? <PhoneNumber number={'+' + phoneNumber}/> : "phoneNumber",
									textTranslated: !!phoneNumber,
									action: () => this._openPhoneKeyboard(),
									disabled: sending,
								},
							]}/>

							<p className={"addendum"}>
								<Interpret id={"receiptHint"}/>
							</p>

							<p className={"personal-info-text"}><Interpret id={"agreeToPersonalInfo"}/></p>
						</>
					}
					contentRightSlot={
						<ButtonsConstructor buttons={[
							{
								className: "confirm-button",
								text: "confirm",
								action: () => this._sendPin(email, phoneNumber),
								disabled: (AppUtils.simulateWizardSessionCreation ? false : (IS.empty(phoneNumber) && IS.empty(email))) || sending,
								spinner: sending,
							}
						]}/>
					}
				/>
			</section>
		);
	}

	_openEmailKeyboard() {
		const {email} = this.state;

		window.keyboard.open(
			E_Keyboard_Type.EMAIL,
			email,
			"email",
			email => this.setState({email}),
			{
				maxLength: 320,
			}
		);
	}

	_openPhoneKeyboard() {
		const {phoneNumber} = this.state;

		window.keyboard.open(
			E_Keyboard_Type.PHONE,
			phoneNumber,
			"phoneNumber",
			phoneNumber => this.setState({phoneNumber}),
			{
				maxLength: 15,
			}
		);
	}

	_sendPin(email, phoneNumber, asTest = false) {
		const {serviceTypeID, bracketID, localizationContext, configuration, onSession} = this.props;
		const {locationID, unitID} = configuration;
		const locale = localizationContext.getActiveLanguageISOCode();

		this.setState({sending: true});

		try {
			if(phoneNumber) {
				let numberInstance = phoneUtil.parseAndKeepRawInput('+' + phoneNumber, 'CZ');

				//Will throw an error if the region is invalid
				phoneUtil.getRegionCodeForNumber(numberInstance);

				phoneNumber = '+' + phoneNumber;
			}
			else {
				phoneNumber = undefined;
			}
		}
		catch (e) {
			phoneNumber = `+420${phoneNumber}`;
		}

		sessionServices.createItem(
			{
				locationID,
				unitID,
				serviceTypeID,
				timeBracketID: bracketID,
				email,
				phone: phoneNumber,
				locale,
			},
			AppUtils.simulateWizardSessionCreation
		).then((session) => {
			onSession(session);

			route(`${E_Route.RENT_WIZARD(E_RentWizard_Section.PIN_TEST)}${asTest ? QS({pin: session.pin}) : ''}`);
		}, (res) => {
			this.setState({sending: false});

			//TBD: More specific error?
			let error = ErrorUtils.getErrorFromResponse(res);
			window.modal.open(
				<MessageModal
					icon={error.icon}
					title={"session.create.error.title"}
					message={"session.create.error.message"}
					actionButtons={[
						{
							text: "retry",
							action: () => {
								window.modal.close();
								this._sendPin(email, phoneNumber, asTest);
							},
							className: "confirm",
						}
					]}
				/>,
				{
					accent: "error",
					namespace: "error",
				},
			);

			applicationThunk.setIdleTimeout();
		});
	}

	static get propTypes() {
		return {
			className: PropTypes.string,
			style: PropTypes.object,
			children: PropTypes.any,

			serviceTypeID: PropTypes.number,
			bracketID: PropTypes.number,

			onSession: PropTypes.func.isRequired,

			//withStorageUnit
			unit: PropTypes.object,

			//withLocalizationContext
			localizationContext: PropTypes.object,

			//withConfiguration
			configuration: PropTypes.object,
		}
	}

	static get stateTypes() {
		return {
			sending: PropTypes.bool,
		}
	}
}

export default withStorageUnit(withLocalizationContext(withConfiguration(RentWizardContact)));
